/**
 * Anchor-aware replacement for @bedrock/ui-patterns ContentfulLink.
 *
 * The upstream @bedrock/core `getLinkData` utility ignores the `anchor` field
 * on Contentful link entries. This component resolves the anchor at render time
 * so that every `<a>` tag shipped from the server already contains the correct
 * hash fragment — no client-side DOM patching required.
 */

import type { ContentfulLinkType } from '@bedrock/core/contentful';
import { Link, type LinkProps } from '@bedrock/ui/link';
import { cn } from '@bedrock/ui/utils';

type BaseNavigationLinkProps = {
  link?: ContentfulLinkType & { anchor?: string };
};

type UnderlineProps = BaseNavigationLinkProps & Omit<LinkProps, 'href'> & { variant: 'underline' };

type DefaultProps = BaseNavigationLinkProps &
  Omit<LinkProps, 'href' | 'variant'> & { variant?: never };

type NavigationLinkProps = UnderlineProps | DefaultProps;

function resolveHref(link: ContentfulLinkType & { anchor?: string }): string {
  const base = link.linkedPage ? `/${link.linkedPage.slug}` : link.url || '';
  const anchor = link.anchor?.replace('#', '');
  return anchor ? `${base}#${anchor}` : base;
}

function resolveTarget(link: ContentfulLinkType): string {
  return link.url ? '_blank' : '_self';
}

export function NavigationLink({
  link,
  children,
  variant,
  leftIcon,
  rightIcon,
  size,
  containerClassName,
  contentClassName,
  className,
  ...props
}: Readonly<NavigationLinkProps>) {
  if (!link) return null;

  const href = resolveHref(link);
  const target = resolveTarget(link);
  const hasAnchor = href.includes('#');

  // For anchor links, use a plain <a> so the browser handles hash scrolling
  // natively. Next.js <Link> intercepts clicks via pushState which prevents
  // the browser from scrolling to the #fragment.
  if (hasAnchor) {
    if (variant === 'underline') {
      return (
        <Link
          className={cn('patterns-contentful-link', className)}
          containerClassName={cn('patterns-contentful-link-container', containerClassName)}
          contentClassName={cn('patterns-contentful-link-content', contentClassName)}
          href={href}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          size={size}
          target={target}
          variant={variant}
          {...props}
        >
          {children}
        </Link>
      );
    }

    return (
      <Link
        className={cn('patterns-contentful-link', className)}
        href={href}
        target={target}
        {...props}
      >
        {children}
      </Link>
    );
  }

  if (variant === 'underline') {
    return (
      <Link
        className={cn('patterns-contentful-link', className)}
        containerClassName={cn('patterns-contentful-link-container', containerClassName)}
        contentClassName={cn('patterns-contentful-link-content', contentClassName)}
        href={href}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        size={size}
        target={target}
        variant={variant}
        {...props}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      className={cn('patterns-contentful-link', className)}
      href={href}
      target={target}
      {...props}
    >
      {children}
    </Link>
  );
}
